import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, CtaBand, Img } from "@/components/Blocks";
import { SERVICES } from "@/data/services";
import { INDUSTRY_PAGES } from "@/data/industries";

const TECH = [
  ["Web", "React, Next.js, responsive front-end engineering, headless CMS when required"],
  ["Applications", "Web applications, APIs, role-based portals and dashboards"],
  ["Mobile", "Android and iOS apps, cross-platform frameworks, offline sync, barcode scanning"],
  ["Data", "Relational and document databases, reporting, business intelligence"],
  ["Integration", "REST APIs, webhooks, file-based data exchange, ERP and accounting connectors"],
  ["Cloud", "Cloud hosting, CI/CD, monitoring, backups and security hardening"],
  ["IoT", "Gateway and platform integrations for machine and sensor data"],
  ["AI", "Document extraction, summarisation and assistants built on established AI services"],
];

export default function About() {
  const crumbs = [["Home", "/"], ["About Us", "/about"]];
  return (
    <>
      <Seo title="About Industrix Solution | Industrial Technology Partner" description="Industrix Solution helps manufacturers and industrial businesses with custom software, websites, ERP, CRM, mobile apps and automation. Learn about our approach and capabilities." path="/about" jsonLd={[breadcrumbSchema(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="About us" lines={["Helping Industrial", "Businesses Move Forward", "With Technology"]} intro="Industrix Solution helps manufacturing companies, industrial suppliers, engineering businesses, and factories strengthen their digital capabilities. We combine website development, custom software, business automation, mobile applications, and digital marketing to help businesses improve their processes and build a stronger digital presence." img="machinist" imgAlt="Technician working on manufacturing equipment" />

      <section className="wrap py-24 lg:py-32" data-testid="about-purpose">
        <div className="grid gap-14 lg:grid-cols-2">
          <SectionHead eyebrow="Our purpose" title="Practical, scalable technology for real industrial work" />
          <Reveal className="space-y-6 text-[16px] leading-relaxed text-graphite">
            <p>Industrix Solution focuses on practical, scalable technology tailored to real business requirements.</p>
            <p>Our approach is built around understanding business challenges, selecting suitable technologies, designing intuitive user experiences, and delivering maintainable digital solutions.</p>
            <p>We work with businesses in India and in international markets, and we keep our recommendations grounded in what will actually be adopted on the shop floor, in the office and by your customers.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-stone bg-white py-24 lg:py-32" data-testid="about-what">
        <div className="wrap grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow="What we do" title="Twelve service areas, one industrial focus" />
            <ul className="mt-10 grid gap-x-6 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="flex items-center justify-between border-b border-stone py-3 text-[15px] hover:text-rust-ink">{s.title}<ArrowUpRight className="h-4 w-4" /></Link></li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHead eyebrow="Industries we support" title="Built around sector realities" />
            <ul className="mt-10 grid gap-x-6 sm:grid-cols-2">
              {INDUSTRY_PAGES.map((i) => (
                <li key={i.slug}><Link to={`/industries/${i.slug}`} className="flex items-center justify-between border-b border-stone py-3 text-[15px] hover:text-rust-ink">{i.name}<ArrowUpRight className="h-4 w-4" /></Link></li>
              ))}
              <li><Link to="/industries" className="flex items-center justify-between border-b border-stone py-3 text-[15px] font-semibold text-rust-ink">All 24 industries<ArrowUpRight className="h-4 w-4" /></Link></li>
            </ul>
          </div>
        </div>
      </section>

      <section className="wrap py-24 lg:py-32" data-testid="about-approach">
        <div className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal className="relative aspect-[4/3] overflow-hidden rounded-md [clip-path:polygon(0_0,88%_0,100%_14%,100%_100%,0_100%)]"><Img name="tools" alt="Industrial tools and machinery components" sizes="(min-width:1024px) 50vw, 100vw" /></Reveal>
          <div>
            <SectionHead eyebrow="Our approach" title="Understand. Select. Design. Deliver." />
            <ol className="mt-10 space-y-5">
              {["Understand the business challenge before proposing technology.", "Select technologies that suit your scale, team and budget.", "Design intuitive journeys for operators, managers and customers.", "Deliver maintainable solutions with documentation and agreed support."].map((t, i) => (
                <Reveal as="li" key={t} delay={i * 0.06} className="flex gap-5 border-b border-stone pb-5 text-[16px]"><span className="font-mono text-xs text-rust-ink pt-1">0{i + 1}</span>{t}</Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-charcoal py-24 text-ivory lg:py-32" data-testid="about-tech">
        <div className="grid-lines-dark absolute inset-0" aria-hidden="true" />
        <div className="wrap relative">
          <SectionHead light eyebrow="Technology capabilities" title="Our Technology Capabilities" />
          <dl className="mt-14 grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {TECH.map(([t, d]) => (
              <div key={t} className="bg-charcoal p-7">
                <dt className="font-mono text-xs uppercase tracking-[0.2em] text-[#E8B795]">{t}</dt>
                <dd className="mt-4 text-[15px] leading-relaxed text-[#E4E0D6]">{d}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <CtaBand title="Let's discuss your next step" />
    </>
  );
}
